/**
 * Error Tracking Utility
 *
 * Provides centralized error logging with environment-aware behavior:
 * - Development: Logs to console
 * - Production: Sends to error tracking service (Sentry, LogRocket, etc.)
 *
 * Usage:
 *   import errorTracker from './utils/errorTracker';
 *   errorTracker.captureException(error, { context: 'user_action' });
 */

class ErrorTracker {
  constructor() {
    this.isProduction = process.env.NODE_ENV === "production";
    this.isDevelopment = process.env.NODE_ENV === "development";
    this.isInitialized = false;
  }

  /**
   * Initialize error tracking service
   * Call this once in index.js or App.js
   */
  init() {
    if (this.isProduction && !this.isInitialized) {
      // Initialize production error tracking service here
      // Example for Sentry:
      /*
      if (window.Sentry && process.env.REACT_APP_SENTRY_DSN) {
        window.Sentry.init({
          dsn: process.env.REACT_APP_SENTRY_DSN,
          environment: process.env.NODE_ENV,
          // ... other config
        });
        this.isInitialized = true;
      }
      */

      // For now, just expose a global hook
      window.errorTracker = this;
      this.isInitialized = true;
    }
  }

  /**
   * Capture an exception
   * @param {Error} error - The error object
   * @param {Object} context - Additional context about the error
   */
  captureException(error, context = {}) {
    if (error == null) return;

    if (this.isDevelopment) {
      console.group("🐛 Error Captured");
      console.error("Error:", error);
      console.log("Context:", context);
      console.log("Stack:", error?.stack);
      console.groupEnd();
      return;
    }

    if (this.isProduction) {
      // Send to error tracking service
      if (window.Sentry) {
        window.Sentry.captureException(error, {
          extra: context,
        });
      } else {
        // Fallback: log to console in production (not ideal, but better than nothing)
        console.error("Error:", error?.message, context);
      }
    }
  }

  /**
   * Capture a message (non-error event)
   * @param {string} message - The message to log
   * @param {string} level - Log level: 'info', 'warning', 'error'
   * @param {Object} context - Additional context
   */
  captureMessage(message, level = "info", context = {}) {
    if (this.isDevelopment) {
      const emoji = { info: "ℹ️", warning: "⚠️", error: "🚨" }[level] || "ℹ️";
      console.log(`${emoji} ${message}`, context);
      return;
    }

    if (this.isProduction && window.Sentry) {
      window.Sentry.captureMessage(message, {
        level,
        extra: context,
      });
    }
  }

  /**
   * Set user context for error tracking
   * @param {Object} user - User information
   */
  setUser(user) {
    if (this.isProduction && window.Sentry) {
      window.Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.username,
      });
    }
  }

  /**
   * Clear user context (e.g., on logout)
   */
  clearUser() {
    if (this.isProduction && window.Sentry) {
      window.Sentry.setUser(null);
    }
  }

  /**
   * Add breadcrumb for debugging
   * @param {string} message - Breadcrumb message
   * @param {Object} data - Additional data
   */
  addBreadcrumb(message, data = {}) {
    if (this.isDevelopment) {
      console.log("🍞 Breadcrumb:", message, data);
      return;
    }

    if (this.isProduction && window.Sentry) {
      window.Sentry.addBreadcrumb({
        message,
        data,
        timestamp: Date.now() / 1000,
      });
    }
  }

  /**
   * Wrap async function with error handling
   * @param {Function} fn - Async function to wrap
   * @param {Object} context - Context for error tracking
   * @returns {Function} Wrapped function
   */
  wrapAsync(fn, context = {}) {
    return async (...args) => {
      try {
        return await fn(...args);
      } catch (error) {
        this.captureException(error, {
          ...context,
          functionName: fn.name,
          arguments: args,
        });
        throw error;
      }
    };
  }
}

// Create singleton instance
const errorTracker = new ErrorTracker();

// Auto-initialize
errorTracker.init();

export default errorTracker;
